import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { cleanTypeScreenServices } from 'services/ReferenceScreenServices/cleanTypeServices';
import { CleanType } from 'Interfaces/ReferenceInterfaces/CleanTypeInterface';

export class CleanTypeStore {
  cleanTypes: CleanType[] = [];
  isLoading: boolean = false;
  pagination = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
  };
  selectedCleanType: CleanType | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  fetchCleanTypes = async (page = 1, pageSize = 15) => {
    this.isLoading = true;
    try {
      const response = await cleanTypeScreenServices.getAllCleanTypes(page, pageSize);
      runInAction(() => {
        this.cleanTypes = response.data;
        this.pagination = {
          currentPage: response.pagination.current_page,
          totalPages: response.pagination.total_pages,
          totalItems: response.pagination.total_items,
          hasNext: response.pagination.has_next,
          hasPrevious: response.pagination.has_previous
        };
      });
    } catch (error) {
      toast.error('Error fetching clean types');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  createCleanType = async (cleanTypeData: Omit<CleanType, 'id' | 'created_at' | 'updated_at'>) => {
    this.isLoading = true;
    try {
      const response = await cleanTypeScreenServices.createCleanType(cleanTypeData);
      if (response.status) {
        toast.success('Clean type created successfully');
        this.fetchCleanTypes(this.pagination.currentPage); // Refresh the list
      } else {
        toast.error(response.message || 'Failed to create clean type');
      }
    } catch (error) {
      toast.error('Error creating clean type');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  updateCleanType = async (
    id: number,
    cleanTypeData: Partial<Omit<CleanType, 'id' | 'created_at' | 'updated_at'>>
  ) => {
    this.isLoading = true;
    try {
      const response = await cleanTypeScreenServices.updateCleanType(id, cleanTypeData);
      if (response.status) {
        toast.success('Clean type updated successfully');
        this.fetchCleanTypes(this.pagination.currentPage); // Refresh the list
      } else {
        toast.error(response.message || 'Failed to update clean type');
      }
    } catch (error) {
      toast.error('Error updating clean type');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  deleteCleanType = async (id: number) => {
    this.isLoading = true;
    try {
      const response = await cleanTypeScreenServices.deleteCleanType(id);
      if (response.status) {
        toast.success('Clean type deleted successfully');
        this.fetchCleanTypes(this.pagination.currentPage); // Refresh the list
      } else {
        toast.error(response.message || 'Failed to delete clean type');
      }
    } catch (error) {
      toast.error('Error deleting clean type');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  setSelectedCleanType = (cleanType: CleanType | null) => {
    this.selectedCleanType = cleanType;
  };
}

export const cleanTypeStore = new CleanTypeStore();
